import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import { customerBookingUrl } from "./customerBookingQr";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { isNodeBookingAvailable } from "../../../utils/nodeAvailability";
import bdsleepLogo from "../../../images/logo/logo_sq.png";

const CustomerBookingQrModal = ({ customer, nodeId, service = "sleep-test", onHide }) => {
  const qrRef = useRef(null);
  const [message, setMessage] = useState("");
  const [nodeCheck, setNodeCheck] = useState({ nodeId: null, status: "checking" });
  useEffect(() => {
    setNodeCheck({ nodeId, status: "checking" });
    return onSnapshot(doc(db, "nodes", nodeId), { includeMetadataChanges: true }, (snapshot) => {
      const status = snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites ? "checking" :
        snapshot.exists() && isNodeBookingAvailable(snapshot.data()) ? "available" : "unavailable";
      setNodeCheck({ nodeId, status });
    }, () => setNodeCheck({ nodeId, status: "error" }));
  }, [nodeId]);
  const nodeStatus = nodeCheck.nodeId === nodeId ? nodeCheck.status : "checking";
  if (!customer) return null;
  const configuredPortalOrigin = process.env.REACT_APP_PATIENT_PORTAL_URL;
  const portalOrigin = !configuredPortalOrigin || [
    "https://sleepy-patient-demo-9b23f.web.app",
    "https://sleepy-patient-demo-9b23f.web.app/",
    "https://sleepy-patient-portal-9b23f.web.app",
    "https://sleepy-patient-portal-9b23f.web.app/",
  ].includes(configuredPortalOrigin)
    ? "https://sleepcare.bdsleep.tech/"
    : configuredPortalOrigin;
  let url = "";
  if (nodeStatus === "available") {
    try { url = customerBookingUrl(portalOrigin, nodeId, customer.id, service); } catch { /* Show configuration message below. */ }
  }
  const localOnly = url && ["127.0.0.1", "localhost", "[::1]"].includes(new URL(url).hostname);
  const name = customer.CustomerNameTH || customer.CustomerNameEN || customer.id;
  const title = service === "cpap" ? "CPAP QR" : "Booking QR";
  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setMessage("คัดลอกลิงก์แล้ว"); }
    catch { setMessage("กรุณาคัดลอกลิงก์จากช่องด้านบน"); }
  };
  const download = () => {
    if (!url) return;
    const canvas = qrRef.current && qrRef.current.querySelector("canvas");
    if (!canvas) return;
    const logo = qrRef.current.querySelector("img");
    if (logo && (!logo.complete || !logo.naturalWidth)) {
      setMessage("โลโก้กำลังโหลด กรุณาลองดาวน์โหลดอีกครั้ง");
      return;
    }
    const anchor = document.createElement("a");
    anchor.href = canvas.toDataURL("image/png");
    anchor.download = `${service === "cpap" ? "cpap" : "booking"}-qr-${customer.id.replace(/[^a-zA-Z0-9_-]/g, "_")}.png`;
    anchor.click();
  };
  return (
    <Modal show onHide={onHide} centered size="lg">
      <Modal.Header closeButton><Modal.Title>{title} — {name}</Modal.Title></Modal.Header>
      <Modal.Body>
        {url && <p>ส่ง QR Code หรือลิงก์นี้ให้ผู้ป่วย{service === "cpap" ? "เพื่อส่งคำขอรับบริการ CPAP" : "เพื่อจองนัดหมาย"} ระบบจะเลือกและล็อกสถานที่เป็น <strong>{name}</strong></p>}
        {url && service === "cpap" && <p>ลิงก์นี้เปิดบริการ CPAP โดยตรง ผู้ป่วยเลือกไปโรงพยาบาล / คลินิกเมื่อสถานที่นี้เปิดบริการวัน OPD หรือให้เจ้าหน้าที่ติดต่อกลับได้</p>}
        {nodeStatus === "checking" ? <p role="status">Checking node availability…</p> :
          nodeStatus !== "available" ? <div role="alert" className="alert alert-warning">{nodeStatus === "error" ? "Unable to verify this node. Please close and try again." : "This node is not accepting new bookings. Reservation QR is unavailable."}</div> :
          !url ? <div role="alert" className="alert alert-warning">ยังไม่ได้ตั้งค่าที่อยู่ Patient Portal กรุณาให้ผู้ดูแลระบบตั้งค่า REACT_APP_PATIENT_PORTAL_URL ก่อนสร้าง QR Code</div> : <>
          {localOnly && <div className="alert alert-warning">ลิงก์ทดสอบเปิดได้บนเครื่องนี้เท่านั้น ต้องตั้งค่าที่อยู่ Patient Portal ที่โทรศัพท์เข้าถึงได้ก่อนส่งให้ผู้ป่วย</div>}
          <div ref={qrRef} className="text-center mb-3"><QRCodeCanvas value={url} size={280} marginSize={4} level="H" imageSettings={{ src: bdsleepLogo, width: 56, height: 56, excavate: true }} title={`${title} ${name}`} style={{ maxWidth: "100%", height: "auto" }} /></div>
          <label className="d-block">ลิงก์จองสำหรับผู้ป่วย<input className="form-control" aria-label="ลิงก์จองสำหรับผู้ป่วย" readOnly value={url} onFocus={(event) => event.target.select()} /></label>
          {message && <p role="status" className="mb-0 mt-2">{message}</p>}
        </>}
      </Modal.Body>
      <Modal.Footer>
        {url && <><Button variant="outline-primary" onClick={copy}>คัดลอกลิงก์</Button><Button variant="primary" onClick={download}>ดาวน์โหลด QR Code</Button><a className="btn btn-light" href={url} target="_blank" rel="noopener noreferrer">เปิดหน้าจอง</a></>}
        <Button variant="light" onClick={onHide}>ปิด</Button>
      </Modal.Footer>
    </Modal>
  );
};
export default CustomerBookingQrModal;
